import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Clock, Package, Sparkle, MapPin, Warning, Printer } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AutocompleteInput } from '@/components/AutocompleteInput';
import { printMovementSlip, printBatchPickSlip } from '@/lib/printSlip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PickingPage({ user }) {
  const formatPickQty = (task) => {
    const p = task.pallets ?? task.quantity ?? 0;
    const c = task.cartons_to_pick ?? task.cartons ?? 0;
    if (p > 0 && c > 0) return `${p} plt${p > 1 ? 's' : ''} + ${c} ctns`;
    if (p > 0) return `${p} plt${p > 1 ? 's' : ''}`;
    if (c > 0) return `${c} ctns (loose)`;
    return '—';
  };
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedInventoryId, setSelectedInventoryId] = useState(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [completing, setCompleting] = useState(false);
  const [formData, setFormData] = useState({
    item_code: '',
    qc_number: '',
    batch_number: '',
    reference: '',
    pallets: '',
    cartons: ''
  });

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.get(`${API}/tasks/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pickingTasks = response.data.filter(t => t.task_type === 'picking');
      setTasks(pickingTasks);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggest = async () => {
    if (!formData.item_code || !formData.pallets || !formData.cartons) {
      toast.error('Item Code, Pallets and Cartons are required');
      return;
    }
    setSuggestionLoading(true);
    try {
      const token = localStorage.getItem('warehouse_token');
      const response = await axios.post(`${API}/picking/suggest-location`, {
        ...formData,
        pallets: parseInt(formData.pallets),
        cartons: parseInt(formData.cartons)
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuggestion(response.data);
      setSelectedLocationId(response.data.best_suggestion?.location_id || null);
      setSelectedInventoryId(response.data.best_suggestion?.inventory_id || null);
      if (response.data.no_stock) {
        toast.error(response.data.message || 'No matching stock found');
      } else if (response.data.best_suggestion && !response.data.best_suggestion.sufficient) {
        toast.warning(`Only ${response.data.best_suggestion.available_qty} pallets available`);
      } else {
        toast.success('Pick location suggested');
      }
    } catch (error) {
      toast.error('Failed to get suggestion');
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleConfirmPicking = async () => {
    if (!formData.reference || !formData.reference.trim()) {
      toast.error('D/O Number is required');
      return;
    }
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.post(`${API}/picking/create`, {
        ...formData,
        pallets: parseInt(formData.pallets) || 0,
        cartons: parseInt(formData.cartons) || 0,
        location_id: selectedLocationId,
        inventory_id: selectedInventoryId
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Picking job added under D/O ${formData.reference}`);
      setShowForm(false);
      setSuggestion(null);
      setSelectedLocationId(null);
      setSelectedInventoryId(null);
      setFormData({ item_code: '', qc_number: '', batch_number: '', reference: '', pallets: '', cartons: '' });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add picking job');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.post(`${API}/picking/complete/${taskId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Picking completed — stock deducted');
      setSelectedTaskIds(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
      fetchTasks();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const toggleTaskSelect = (taskId) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  const toggleSelectAll = () => {
    if (selectedTaskIds.size === pendingTasks.length && pendingTasks.length > 0) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(pendingTasks.map(t => t.id)));
    }
  };

  const handlePrintSelected = () => {
    if (selectedTaskIds.size === 0) {
      toast.error('Please tick at least one picking job');
      return;
    }
    const selected = pendingTasks.filter(t => selectedTaskIds.has(t.id));
    // Use the actual D/O numbers keyed by warehouse staff. If mixed, join them.
    const doList = [...new Set(selected.map(t => t.do_number || t.reference).filter(Boolean))];
    const doNumber = doList.length === 0
      ? 'NO-DO'
      : doList.length === 1 ? doList[0] : doList.join(' + ');
    printBatchPickSlip(doNumber, selected.map(t => ({
      item_code: t.item_code,
      qc_number: t.qc_number,
      batch_number: t.batch_number,
      pallets: t.pallets || t.quantity,
      cartons: t.cartons_to_pick || t.cartons,
      location_code: t.from_location?.code
    })));
    toast.success(`Forklift instructions printed for D/O ${doNumber}`);
  };

  const handleUndoPending = async (task) => {
    if (!window.confirm(`Cancel pending pick: ${task.item_code} · ${task.pallets || 0} plt + ${task.cartons_to_pick || 0} ctns (D/O ${task.do_number || task.reference || '—'})?\n\nThis removes the task. No stock has been deducted yet.`)) return;
    try {
      const token = localStorage.getItem('warehouse_token');
      await axios.delete(`${API}/picking/${task.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pending pick cancelled');
      setSelectedTaskIds(prev => { const n = new Set(prev); n.delete(task.id); return n; });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel pick');
    }
  };

  const handleCompleteSelected = async () => {
    if (selectedTaskIds.size === 0) {
      toast.error('Please tick at least one picking job');
      return;
    }
    setCompleting(true);
    const token = localStorage.getItem('warehouse_token');
    const ids = Array.from(selectedTaskIds);
    const failed = [];
    for (const id of ids) {
      try {
        await axios.post(`${API}/picking/complete/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('complete failed for', id, err?.response?.data || err.message);
        failed.push({ id, msg: err?.response?.data?.detail || err.message });
      }
    }
    const okCount = ids.length - failed.length;
    if (okCount > 0) toast.success(`${okCount} pick(s) completed — stock deducted`);
    if (failed.length > 0) toast.error(`${failed.length} pick(s) failed: ${failed[0].msg}`);
    setSelectedTaskIds(new Set());
    setCompleting(false);
    fetchTasks();
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="picking-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="picking-title">
            Picking
          </h1>
          <p className="text-[#52525B]">Add single picks, tick to group them into a forklift instruction sheet</p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setSuggestion(null); }}
          className="btn-primary flex items-center gap-2"
          data-testid="new-picking-button"
        >
          <Package size={18} weight="bold" />
          {showForm ? 'Close Form' : 'New Picking'}
        </button>
      </div>

      {showForm && (
        <div className="card mb-6" data-testid="picking-form">
          <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Register Picking Request</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AutocompleteInput
              id="item_code"
              label="Item Code"
              field="item_code"
              value={formData.item_code}
              onChange={(v) => setFormData({ ...formData, item_code: v })}
              context={formData}
              placeholder="ITM-001"
              testId="picking-item-code-input"
              required
            />
            <AutocompleteInput
              id="qc_number"
              label="QC / Shade"
              field="qc_number"
              value={formData.qc_number}
              onChange={(v) => setFormData({ ...formData, qc_number: v })}
              context={formData}
              placeholder="QC-Blue-01"
              testId="picking-qc-input"
            />
            <AutocompleteInput
              id="batch_number"
              label="Batch Number"
              field="batch_number"
              value={formData.batch_number}
              onChange={(v) => setFormData({ ...formData, batch_number: v })}
              context={formData}
              placeholder="BATCH-2026-001"
              testId="picking-batch-input"
            />
            <AutocompleteInput
              id="reference"
              label="D/O Number *"
              field="reference"
              value={formData.reference}
              onChange={(v) => setFormData({ ...formData, reference: v })}
              context={formData}
              placeholder="DO-12345"
              testId="picking-reference-input"
              required
            />
            <div className="md:col-span-2">
              <Label>Quantity to Pick *</Label>
              <div className="flex items-center gap-2">
                <Input id="pallets" type="number" min="0" value={formData.pallets} onChange={(e) => setFormData({ ...formData, pallets: e.target.value })} placeholder="0" className="mono flex-1" data-testid="picking-pallets-input" />
                <span className="text-sm font-medium text-[#52525B] mono">full plts</span>
                <span className="text-lg text-[#52525B]">+</span>
                <Input id="cartons" type="number" min="0" value={formData.cartons} onChange={(e) => setFormData({ ...formData, cartons: e.target.value })} placeholder="0" className="mono flex-1" data-testid="picking-cartons-input" />
                <span className="text-sm font-medium text-[#52525B] mono">loose ctns</span>
              </div>
              <p className="text-xs text-[#52525B] mt-1 mono">
                Full pallets pull complete pallets. Loose cartons peel that many cartons from a chosen pallet (partial pallet stays at the slot).
              </p>
            </div>
          </div>

          <div className="mt-6 flex gap-3">
            <button onClick={handleSuggest} disabled={suggestionLoading} className="btn-secondary flex items-center gap-2" data-testid="suggest-pick-location-button">
              <Sparkle size={18} weight="fill" />
              {suggestionLoading ? 'Analyzing...' : 'Suggest Pick Location'}
            </button>
            {suggestion && !suggestion.no_stock && suggestion.best_suggestion?.sufficient && (
              <button onClick={handleConfirmPicking} className="btn-primary flex items-center gap-2" data-testid="confirm-picking-button">
                <CheckCircle size={18} weight="bold" />
                Confirm & Add to Pick List
              </button>
            )}
          </div>

          {suggestion && suggestion.best_suggestion && (
            <div className="mt-6 p-4 bg-[#007AFF]/10 border-2 border-[#007AFF] rounded-md" data-testid="pick-suggestion-card">
              <p className="text-xs font-bold text-[#007AFF] uppercase tracking-wide mb-3">Select a location to pick from</p>
              <div className="space-y-2">
                {suggestion.suggestions.map((s, i) => {
                  const isSelected = selectedInventoryId === s.inventory_id;
                  const isRecommended = i === 0;
                  const disabled = !s.sufficient;
                  return (
                    <button key={s.inventory_id || s.location_id} type="button" onClick={() => { if (!disabled) { setSelectedInventoryId(s.inventory_id); setSelectedLocationId(s.location_id); } }} disabled={disabled}
                      className={`w-full text-left p-3 rounded border-2 transition-all duration-150 ${isSelected ? 'bg-white border-[#007AFF] shadow-sm' : disabled ? 'bg-[#F4F4F5] border-[#E4E4E7] opacity-60 cursor-not-allowed' : 'bg-white/60 border-[#E4E4E7] hover:border-[#007AFF]/50'}`}
                      data-testid={`pick-location-option-${i}`}>
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${isSelected ? 'border-[#007AFF] bg-[#007AFF]' : 'border-[#A1A1AA]'}`}>
                          {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <span className="mono text-base font-black text-[#09090B]">{s.location_code}</span>
                              {isRecommended && !disabled && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-sm bg-[#007AFF] text-white uppercase tracking-wide">Recommended</span>}
                              {disabled && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-sm bg-[#F59E0B] text-white uppercase tracking-wide">Insufficient</span>}
                              {s.available_qty === 1 && (s.available_cartons_per_pallet || 0) > 0 && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-sm bg-[#F59E0B] text-white uppercase tracking-wide">Partial</span>}
                            </div>
                            <span className="text-xs mono font-bold text-[#09090B]">{s.available_qty} plt × {s.available_cartons_per_pallet || 0} ctns</span>
                          </div>
                          <p className="text-xs text-[#52525B]">{s.reason}</p>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#52525B]">
                            <span>Zone: <span className="mono font-bold text-[#09090B]">{s.zone}</span></span>
                            {s.qc_number && <span>QC: <span className="mono font-bold text-[#09090B]">{s.qc_number}</span></span>}
                            {s.batch_number && <span>Batch: <span className="mono font-bold text-[#09090B]">{s.batch_number}</span></span>}
                            {s.date_in && <span>In: <span className="mono text-[#09090B]">{s.date_in}</span></span>}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="card bg-[#F59E0B]/5 border-[#F59E0B]/20">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-[#F59E0B]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Pending Picks</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="picking-pending-count">{pendingTasks.length}</p>
            </div>
          </div>
        </div>
        <div className="card bg-[#007AFF]/5 border-[#007AFF]/20">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-[#007AFF]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Selected</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="selected-count">{selectedTaskIds.size}</p>
            </div>
          </div>
        </div>
        <div className="card bg-[#16A34A]/5 border-[#16A34A]/20">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-[#16A34A]" weight="duotone" />
            <div>
              <p className="text-sm text-[#52525B]">Completed</p>
              <p className="heading text-2xl font-black text-[#09090B]" data-testid="picking-completed-count">{completedTasks.length}</p>
            </div>
          </div>
        </div>
      </div>

      {pendingTasks.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="heading text-xl font-bold text-[#09090B]">Pending Picking Jobs</h2>
            <div className="flex gap-2">
              <button onClick={toggleSelectAll} className="btn-secondary text-sm" data-testid="select-all-button">
                {selectedTaskIds.size === pendingTasks.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handlePrintSelected}
                disabled={selectedTaskIds.size === 0}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="print-selected-button"
              >
                <Printer size={18} weight="bold" />
                Print Forklift Instructions ({selectedTaskIds.size})
              </button>
              {selectedTaskIds.size > 0 && (
                <button
                  onClick={handleCompleteSelected}
                  disabled={completing}
                  className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="complete-selected-button"
                >
                  <CheckCircle size={18} weight="bold" />
                  {completing ? 'Completing…' : `Mark Selected Done (${selectedTaskIds.size})`}
                </button>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {pendingTasks.map((task) => {
              const isChecked = selectedTaskIds.has(task.id);
              return (
                <div
                  key={task.id}
                  className={`card cursor-pointer transition-all duration-150 ${isChecked ? 'border-[#007AFF] bg-[#007AFF]/5 shadow-sm' : 'hover:border-[#007AFF]/40'}`}
                  onClick={() => toggleTaskSelect(task.id)}
                  data-testid="picking-task-card"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`mt-1 w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${isChecked ? 'bg-[#007AFF] border-[#007AFF]' : 'border-[#A1A1AA] bg-white'}`}
                      data-testid={`task-checkbox-${task.id}`}
                    >
                      {isChecked && <CheckCircle size={14} weight="fill" className="text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="mono text-sm font-bold text-[#007AFF]" data-testid="picking-task-item-code">{task.item_code}</span>
                        {task.reference && <span className="text-xs bg-[#F4F4F5] text-[#52525B] px-2 py-0.5 rounded-sm mono">Ref: {task.reference}</span>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-[#52525B] text-xs">Quantity</p>
                          <p className="mono font-bold text-[#09090B]" data-testid="picking-task-quantity">
                            {formatPickQty(task)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[#52525B] text-xs">Pick From</p>
                          <p className="mono font-bold text-[#007AFF]" data-testid="picking-task-location">{task.from_location?.code || 'N/A'}</p>
                        </div>
                        {task.qc_number && (
                          <div>
                            <p className="text-[#52525B] text-xs">QC / Shade</p>
                            <p className="mono font-bold text-[#09090B]" data-testid="picking-task-qc">{task.qc_number}</p>
                          </div>
                        )}
                        {task.batch_number && (
                          <div>
                            <p className="text-[#52525B] text-xs">Batch</p>
                            <p className="mono font-bold text-[#09090B]" data-testid="picking-task-batch">{task.batch_number}</p>
                          </div>
                        )}
                        <div className="flex items-end justify-end gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUndoPending(task); }}
                            className="text-xs px-3 py-1 rounded-md border border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] font-medium"
                            data-testid="picking-task-undo-button"
                          >
                            Undo
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCompleteTask(task.id); }}
                            className="btn-secondary text-xs px-3 py-1"
                            data-testid="picking-task-complete-button"
                          >
                            Mark Done
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {pendingTasks.length === 0 && !showForm && (
        <div className="card text-center py-12" data-testid="no-picking-tasks">
          <CheckCircle size={48} className="mx-auto text-[#16A34A] mb-4" weight="duotone" />
          <h3 className="heading text-xl font-bold text-[#09090B] mb-2">No pending picks</h3>
          <p className="text-[#52525B]">Click "New Picking" to add a job to the pick list</p>
        </div>
      )}

      {completedTasks.length > 0 && (
        <div>
          <h2 className="heading text-xl font-bold text-[#09090B] mb-4">Recently Completed</h2>
          <div className="space-y-3">
            {completedTasks.slice(0, 5).map((task) => (
              <div key={task.id} className="card opacity-60" data-testid="picking-completed-task-card">
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-[#16A34A]" weight="fill" />
                  <span className="mono text-sm font-bold text-[#09090B]">{task.item_code || task.item?.sku}</span>
                  <span className="text-sm text-[#52525B] ml-auto">
                    <span className="mono">{formatPickQty(task)}</span> from <span className="mono">{task.from_location?.code}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
